/**
 * ResourceTableSkeleton Component (T079 - US3)
 * Loading skeleton for resource table
 */

type ResourceTableSkeletonProps = {
  rows?: number;
};

/**
 * Skeleton component displayed while resource table data is loading
 * 
 * @param rows - Number of skeleton rows to display (default: 10)
 */
export function ResourceTableSkeleton({ rows = 10 }: ResourceTableSkeletonProps) {
  return (
    <div className="space-y-3">
      {/* Total count skeleton */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 rounded-md border border-gray-200">
        <div className="h-4 w-48 bg-gray-200 rounded animate-pulse" />
      </div>

      {/* Table container */}
      <div className="border border-gray-200 rounded-md overflow-hidden">
        {/* Table header skeleton */}
        <div className="flex items-center gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" /> {/* Title */}
          <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" /> {/* Type */}
          <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" /> {/* Category */}
          <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" /> {/* Status */}
          <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" /> {/* Author */}
          <div className="h-4 w-16 bg-gray-200 rounded animate-pulse" /> {/* Views */}
          <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" /> {/* Published */}
          <div className="h-4 w-16 bg-gray-200 rounded animate-pulse ml-auto" /> {/* Actions */}
        </div>

        {/* Table rows skeleton */}
        {Array.from({ length: rows }).map((_, index) => (
          <div
            key={index}
            className="flex items-center gap-4 px-4 py-4 bg-white border-b border-gray-200 last:border-b-0"
          >
            {/* Title with featured indicator skeleton */}
            <div className="flex items-start gap-2 w-80">
              <div className="h-4 w-4 bg-gray-200 rounded animate-pulse flex-shrink-0" />
              <div className="flex-1 space-y-1.5">
                <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
                <div className="h-3 w-3/4 bg-gray-100 rounded animate-pulse" />
              </div>
            </div>
            
            {/* Type badge skeleton */}
            <div className="h-6 w-20 bg-gray-200 rounded-full animate-pulse" />
            
            {/* Category skeleton */}
            <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
            
            {/* Status badge skeleton */}
            <div className="h-6 w-20 bg-gray-200 rounded-full animate-pulse" />
            
            {/* Author skeleton */}
            <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
            
            {/* Views skeleton */}
            <div className="flex items-center gap-1">
              <div className="h-4 w-4 bg-gray-200 rounded animate-pulse" />
              <div className="h-4 w-12 bg-gray-200 rounded animate-pulse" />
            </div>
            
            {/* Published date skeleton */}
            <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
            
            {/* Actions skeleton */}
            <div className="flex gap-2 ml-auto">
              <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
              <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
